/*customizations:
- aggiunta la verifica del commento obbligatorio a seconda dell'opzione scelta,  commentRequired
- aggiunti attributi positive e commentRequirred a CommentsStep
- aggiunto un ulteriore step in cui si richiede email e privacy nel caso sia stata scelta l'opzione OTHER_POSITIVE o OTHER_NEGATIVE'
- aggiunta una ulteriore card con le info di contatto
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useIntl, defineMessages } from 'react-intl';
import { isCmsUi } from '@plone/volto/helpers';
import {
  Container,
  Row,
  Col,
  Spinner,
  Card,
  CardHeader,
  CardBody,
} from 'design-react-kit/dist/design-react-kit';
import {
  getTranslatedQuestion,
  HoneypotWidget,
  GoogleReCaptchaWidget,
  submitFeedback,
  resetSubmitFeedback,
  getFeedbackThreshold,
  isFeedbackEnabledForRoute,
  getStaticFeedbackRouteTitle,
} from 'volto-feedback';

import cx from 'classnames';
import { UniversalLink } from '@plone/volto/components';
import AnswersStep from './Steps/AnswersStep';
import CommentsStep from './Steps/CommentsStep';
import EmailStep from './Steps/EmailStep';
import RTRating from './Steps/Commons/Rating';
import { PropTypes } from 'prop-types';

const messages = defineMessages({
  title: {
    id: 'feedback_form_title',
    defaultMessage: 'How clear is the information on this page?',
  },
  service_title: {
    id: 'feedback_form_title_service',
    defaultMessage: 'How easy was it to use this service?',
  },
  yes: {
    id: 'feedback_form_yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'feedback_form_no',
    defaultMessage: 'No',
  },
  submit: {
    id: 'feedback_form_submit',
    defaultMessage: 'Submit your comment',
  },
  thank_you: {
    id: 'feedback_form_thank_you',
    defaultMessage: 'Thank you for your feedback!',
  },
  next: {
    id: 'feedback_form_button_next',
    defaultMessage: 'Next',
  },
  prev: {
    id: 'feedback_form_button_prev',
    defaultMessage: 'Previous',
  },
  feedback_sent: {
    id: 'feedback_sent',
    defaultMessage: 'Your feedback was sent!',
  },
  unclear_instructions: {
    id: 'feedback_unclear_instructions',
    defaultMessage: 'Some instructions were not clear and confusin',
  },
  incomplete_instructions: {
    id: 'feedback_incomplete_instructions',
    defaultMessage: 'Some instructions were incomplete',
  },
  technical_problems: {
    id: 'feedback_technical_problems',
    defaultMessage: 'I ran into technical problems',
  },
  other_negative: {
    id: 'feedback_other_negative',
    defaultMessage: 'Other',
  },
  clear_instructions: {
    id: 'feedback_clear_instructions',
    defaultMessage: 'The instructions were clear',
  },
  complete_instructions: {
    id: 'feedback_complete_instructions',
    defaultMessage: 'The instructions were complete',
  },

  no_technical_problems: {
    id: 'feedback_no_technical_problems',
    defaultMessage: 'I had no technical problems',
  },
  other_positive: {
    id: 'feedback_other_positive',
    defaultMessage: 'Other',
  },
  feedback: {
    id: 'feedback_stars',
    defaultMessage: 'Valuta da 1 a 5 stelle',
  },
  error: {
    id: 'feedback_error',
    defaultMessage: 'Error',
  },
  contacts_info_contacts: {
    id: 'feedback_form_contact_infos-contact',
    defaultMessage: "Contatta l'Azienda Usl di Piacenza",
  },
  contacts_info_contacts_description: {
    id: 'feedback_form_contact_infos-contact-description',
    defaultMessage: 'Centralino unico',
  },
  contacts_info_urp: {
    id: 'feedback_form_contact_infos-urp',
    defaultMessage: 'Segnala un problema o richiedi informazioni',
  },
  contacts_info_urp_description: {
    id: 'feedback_form_contact_infos-urp-description',
    defaultMessage: 'Urp - Ufficio relazioni con il pubblico',
  },
});

const FeedbackForm = ({ contentType, pathname }) => {
  const intl = useIntl();
  const location = useLocation();
  const path = pathname ?? location.pathname ?? '/';
  const dispatch = useDispatch();
  const [satisfaction, setSatisfaction] = useState(null);
  const [step, setStep] = useState(0);
  const [invalidForm, setInvalidForm] = useState(true);
  const [formData, setFormData] = useState({});
  const captcha = !!process.env.RAZZLE_RECAPTCHA_KEY ? 'GoogleReCaptcha' : null;
  const submitResults = useSelector((state) => state.submitFeedback);
  const [validToken, setValidToken] = useState(null);
  const threshold = getFeedbackThreshold();
  const fieldHoney = __CLIENT__
    ? window.env.RAZZLE_HONEYPOT_FIELD
    : process.env.RAZZLE_HONEYPOT_FIELD;

  const [numberOfSteps, setNumberOfSteps] = useState(3);

  const changeSatisfaction = (e) => {
    setSatisfaction(e);
  };

  const getFormFieldValue = (field) => formData?.[field] ?? undefined;

  const commentRequired = () => {
    const positive = satisfaction > threshold;
    const _answer = getFormFieldValue('answer');
    return !positive || (positive && _answer === 'other_positive');
  };

  const validateStep = () => {
    switch (step) {
      case 0:
        if (!getFormFieldValue('answer')) {
          setInvalidForm(true);
        } else {
          setInvalidForm(false);
        }
        break;
      case 1:
        const comment = getFormFieldValue('comment');
        let required = false;
        let commentLengthIsOk = true;
        if (commentRequired()) {
          if (comment?.length <= 0) {
            required = true;
          }
        }

        if (comment?.length > 200) {
          commentLengthIsOk = false;
        }

        if (!commentLengthIsOk || required) {
          setInvalidForm(true);
        } else {
          setInvalidForm(false);
        }

        break;
      case 2:
        const email = getFormFieldValue('email');
        const privacy = getFormFieldValue('privacy');
        if (!email || !privacy) {
          setInvalidForm(true);
        } else {
          setInvalidForm(false);
        }
        break;
      default:
        break;
    }
  };

  const updateFormData = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const nextStep = () => {
    if (step === 0) {
      if (['other_positive', 'other_negative'].indexOf(formData.answer) >= 0) {
        //gli step sono 3
        setNumberOfSteps(3);
      } else {
        //gli step sono 2
        setNumberOfSteps(2);
      }
    }
    if (!invalidForm) setStep(step + 1);
  };

  const prevStep = () => {
    if (step !== 0) {
      setStep(step - 1);
    }
  };

  useEffect(() => {
    validateStep();
  }, [formData, step]);

  useEffect(() => {
    setValidToken(null);
    setSatisfaction(null);
    resetFormData();
    return () => {
      setSatisfaction(null);
      setValidToken(null);
      resetFormData();
      dispatch(resetSubmitFeedback());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    const currentVote = getFormFieldValue('vote');
    let new_form_data = { ...formData };
    if (
      (currentVote > threshold && satisfaction < threshold) ||
      (currentVote < threshold && satisfaction > threshold)
    ) {
      new_form_data = { [fieldHoney]: '' };
    }

    new_form_data.vote = satisfaction ?? null;
    setFormData({ ...new_form_data });
    setStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [satisfaction]);

  // initialized honeypot field
  useEffect(() => {
    updateFormData(fieldHoney, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldHoney]);

  // Motivazioni del focus programmatico
  //  - su alcuni browser viene dichiarato "object replacement character" ai rerender di react
  //    che sono inevitabili mentre il focus rimane sul bottone ma non viene detto
  //  - replichiamo 1:1 il comportamento del secondo step, quando vai avanti hai autofocus sulla
  //    textarea per i commenti
  //  - essendo annunciato ora il titolo dello step, l'utente sr sa comunque benissimo dove si trova,
  //    anzi, il flow di compilazione del form kb+sr e' molto piu' agevole
  useEffect(() => {
    if (step === 0 && getFormFieldValue('answer')) {
      const selectedAnswer = document.getElementById(
        `${
          satisfaction > threshold ? 'positive' : 'negative'
        }-${getFormFieldValue('answer')}`,
      );
      if (selectedAnswer) {
        selectedAnswer.focus();
      }
    }

    if (
      step === 1 &&
      commentRequired() &&
      (getFormFieldValue('comment') ?? '')?.length == 0
    ) {
      setInvalidForm(true);
    }

    if (
      step === 2 &&
      (getFormFieldValue('email') ?? '')?.length == 0 &&
      !!getFormFieldValue('privacy')
    ) {
      setInvalidForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const onVerifyCaptcha = useCallback(
    (token) => {
      if (satisfaction !== null && !validToken) {
        setValidToken(token);
      }
    },
    [satisfaction, setValidToken, validToken],
  );

  const resetFormData = () => {
    setFormData({
      [fieldHoney]: '',
    });
  };

  const sendFormData = () => {
    if (invalidForm) return;
    setStep(3);
    let content =
      isFeedbackEnabledForRoute(path) && isCmsUi(path)
        ? getStaticFeedbackRouteTitle(path)
        : path;
    if (typeof content === 'object' && content.id) {
      content = intl.formatMessage(content);
    }

    const data = {
      ...formData,
      ...(captcha && { 'g-recaptcha-response': validToken }),
      answer: getTranslatedQuestion(intl, formData.answer),
      answer_key: formData.answer,
      content,
    };
    console.log(data);
    dispatch(submitFeedback(data));
    resetFormData();
  };

  if (!isFeedbackEnabledForRoute(path)) {
    return null;
  }

  let action = path?.length > 1 ? path.replace(/\//g, '') : path;
  if (action?.length > 0) {
    action = action?.replace(/-/g, '_');
  } else {
    action = 'homepage';
  }

  if (isCmsUi(path)) {
    return null;
  }

  const URP_LINK = {
    it: '/it/orientamento-e-contatti/ufficio-relazioni-con-il-pubblico/scrivi-a-azienda',
    en: '/it/orientamento-e-contatti/ufficio-relazioni-con-il-pubblico/scrivi-a-azienda',
  };

  return (
    <section className="bg-primary feedback-form-wrapper full-width">
      <Container>
        <Row className="d-flex justify-content-center bg-primary">
          <Col className="col-12 col-lg-7">
            <div
              className="feedback-form"
              role="form"
              aria-label={intl.formatMessage(messages.title)}
            >
              <Card
                className="shadow card-wrapper no-after py-3"
                data-element="feedback"
              >
                {!submitResults?.loading &&
                  !submitResults?.loaded &&
                  step !== 3 && (
                    <>
                      <h2
                        id="vf-radiogroup-label"
                        className="title-medium-2-semi-bold mb-0"
                        data-element="feedback-title"
                      >
                        {/* Il validatore a quanto pare fa il check per titolo.
                            Il titolo da specifiche deve essere diverso per Servizi, ma loro non lo sanno
                            https://github.com/italia/pa-website-validator/blob/main/src/storage/municipality/feedbackComponentStructure.ts#L8
                        */}
                        {/* {contentType === 'Servizio'
                          ? intl.formatMessage(messages.service_title)
                          : intl.formatMessage(messages.title)} */}

                        {intl.formatMessage(messages.title)}
                      </h2>
                      <div className="rating-container">
                        <RTRating
                          name="satisfaction"
                          value={satisfaction}
                          inputs={[
                            {
                              name: 'star1b',
                              value: 1,
                            },
                            {
                              name: 'star2b',
                              value: 2,
                            },
                            {
                              name: 'star3b',
                              value: 3,
                            },
                            {
                              name: 'star4b',
                              value: 4,
                            },
                            {
                              name: 'star5b',
                              value: 5,
                            },
                          ]}
                          aria-controls={
                            satisfaction > threshold
                              ? 'vf-more-positive'
                              : 'vf-more-negative'
                          }
                          className="volto-feedback-rating mb-0"
                          onChangeRating={changeSatisfaction}
                          legend={intl.formatMessage(messages.feedback)}
                          wrapperClassName={'rating-label mb-3'}
                        />
                      </div>
                      <AnswersStep
                        updateFormData={updateFormData}
                        userFeedback={satisfaction}
                        intl={intl}
                        step={step}
                        totalSteps={numberOfSteps}
                        getFormFieldValue={getFormFieldValue}
                      />
                      <CommentsStep
                        updateFormData={updateFormData}
                        userFeedback={satisfaction}
                        intl={intl}
                        step={step}
                        totalSteps={numberOfSteps}
                        getFormFieldValue={getFormFieldValue}
                        positive={satisfaction > threshold}
                        commentRequired={commentRequired}
                      />
                      <EmailStep
                        updateFormData={updateFormData}
                        userFeedback={satisfaction}
                        intl={intl}
                        step={step}
                        totalSteps={numberOfSteps}
                        getFormFieldValue={getFormFieldValue}
                      />
                      <HoneypotWidget
                        updateFormData={updateFormData}
                        field={fieldHoney}
                      />
                      <GoogleReCaptchaWidget
                        key={action}
                        onVerify={onVerifyCaptcha}
                        action={action}
                      />
                      <div
                        className={cx(
                          'form-step-actions flex-nowrap w100 justify-content-center button-shadow',
                          {
                            'pt-4 d-flex': satisfaction,
                            'd-none': !satisfaction,
                          },
                        )}
                        aria-hidden={!satisfaction}
                      >
                        {/* Bug bottoni del kit. Disabled e' settato anche se compare la prop aria-disabled,
                        quando lo scopo sarebbe continuare a poter usufruire dei focus anche in screen reader.
                        Per i vedenti, la classe dimmed fa il suo lavoro e disabilita i click/input utente */}
                        <button
                          type="button"
                          onClick={prevStep}
                          disabled={false}
                          className={cx(
                            'mr-4 fw-bold btn btn-outline-primary',
                            {
                              disabled: step === 0,
                            },
                          )}
                          aria-disabled={!(!invalidForm && step !== 0)}
                        >
                          {intl.formatMessage(messages.prev)}
                        </button>

                        {step !== numberOfSteps - 1 && (
                          <button
                            className={cx('fw-bold btn btn-primary', {
                              disabled: invalidForm,
                            })}
                            type="button"
                            onClick={nextStep}
                            disabled={false}
                            aria-disabled={invalidForm}
                          >
                            {intl.formatMessage(messages.next)}
                          </button>
                        )}
                        {step === numberOfSteps - 1 && (
                          <button
                            className={cx('fw-bold btn btn-primary', {
                              disabled: invalidForm,
                            })}
                            type="submit"
                            disabled={false}
                            aria-disabled={invalidForm}
                            onClick={sendFormData}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !invalidForm)
                                sendFormData();
                            }}
                          >
                            {intl.formatMessage(messages.next)}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                {submitResults?.loading && (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner double active />
                  </div>
                )}
                {step == 3 && (
                  <>
                    {submitResults?.loaded && (
                      <CardHeader className="border-0 mb-0 px-0">
                        <h4
                          id="rating-feedback"
                          className="title-medium-2-semi-bold mb-0"
                        >
                          {intl.formatMessage(messages.thank_you)}
                        </h4>
                      </CardHeader>
                    )}

                    {!submitResults?.loaded &&
                      !submitResults?.loading &&
                      submitResults?.error?.response?.body?.message && (
                        <>
                          <CardHeader className="border-0 mb-0 px-0">
                            <h4
                              id="rating-feedback"
                              className="title-medium-2-semi-bold mb-0"
                            >
                              {intl.formatMessage(messages.error)}{' '}
                              {submitResults.error?.response.status}:{' '}
                              {submitResults.error?.response.statusText}
                            </h4>
                          </CardHeader>
                          <CardBody>
                            {submitResults.error?.response?.body?.message}
                          </CardBody>
                        </>
                      )}
                  </>
                )}
              </Card>

              {/* info di contatto */}
              <div className="row contacts-info-card mt-0 mt-lg-4 px-lg-4">
                <Card className="shadow no-after p-3" wrapperClassName="col">
                  <CardBody className="p-0 text-center">
                    <h2 className="mb-0">
                      {intl.formatMessage(messages.contacts_info_contacts)}
                    </h2>
                    {intl.formatMessage(
                      messages.contacts_info_contacts_description,
                    )}{' '}
                    <a href="tel:0523301111">0523.301111</a>
                  </CardBody>
                </Card>

                <Card className="shadow no-after p-3" wrapperClassName="col">
                  <CardBody className="p-0 text-center">
                    <h2 className="mb-0">
                      {intl.formatMessage(messages.contacts_info_urp)}
                    </h2>
                    <UniversalLink href={URP_LINK[intl.locale]}>
                      {intl.formatMessage(
                        messages.contacts_info_urp_description,
                      )}
                    </UniversalLink>
                  </CardBody>
                </Card>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

FeedbackForm.propTypes = {
  contentType: PropTypes.string,
  pathname: PropTypes.string,
};

export default FeedbackForm;
